// @givmo/board — who was actually in the room.
//
// The opposite number of the RSVP (`board_attendance_intent`, RC2): that is what a director SAID they
// would do, this is what happened. Both are shown, side by side and never sharing a word, because the
// difference between them is exactly the thing a secretary is reconciling while writing this down.
//
// Three model facts drive the shape:
//
//   * **ALWAYS THE OFFICER'S ENTRY.** `recorded_by_user_id` is NOT NULL on the backend and there is no
//     director-facing write path — a director does not self-report having been in a room. That is the
//     reverse of the RSVP, where the director's own answer is first-class. So this panel is the
//     officer's data entry, and the RSVP column beside it is read-only reference.
//   * **`arrived_at` / `departed_at` ARE INSTANTS, NOT STATES.** "Arrived late" and "left early" are
//     both true of one seat that did both, which four mutually-exclusive labels cannot express — and
//     quorum-at-vote asks only whether the seat was in the room at the instant the question was put.
//     So the form takes two optional times beside a present/absent value, never a four-way status.
//   * **THE SEAT NEED NOT BE ACTIVE.** A director removed in the autumn was in the room in the spring,
//     and an organization recording that meeting afterwards has a true fact to record. RC2's RSVP path DOES
//     require an active seat, because an RSVP is about a meeting still ahead; this one is about what
//     already happened. So removed seats stay enterable here, marked as no longer serving: the panel
//     reads the WHOLE roster (`include_removed`) and lists the seats that served on the meeting's day
//     (CBG-FIX41, `seatsServingOn`), plus any seat whose attendance is already recorded.
//
// Attendance is NOT confidentiality-scoped — who came to a board meeting is not closed-session
// content. What was said and decided inside a closed session is, and that lives on the sessions panel.
//
// WHO PRESIDED heads the list (CBG-YR3-ROLES-PRESIDING, `who-presided.tsx`): one person, recorded by
// the officer from the people recorded as present here — a fact on their attendance row, never taken
// from a title.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  isGivmoError,
  type OrgBoardAttendanceIntentOut,
  type BoardMeetingAttendanceOut,
  type OrgBoardMemberOut,
  type BoardMeetingAttendanceValue,
} from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorMessage,
  Select,
  TextField,
  toast,
} from '@givmo/design-system';
import { extractList } from '@givmo/api-client/list';
import { FieldNote } from '../../components/field-note';
import { displayDate, displayInstant, todayAsDateInput } from '../../lib/display';
import { intentCaptureLine } from '../meetings/meetings-decode';
import {
  attendanceBySeat,
  attendanceLabel,
  attendanceTone,
  extractAttendance,
  intentLabel,
  seatsServingOn,
  type MeetingDay,
} from './in-meeting-decode';
import { sortSeats } from '../roster/roster-decode';
import { capacityLine, isDirector, keptTogether, termEndedBefore } from '../roster/officer-roles';
import { officerRolesL3Words as l3 } from '../roster/officer-roles-l3-words';
import { useBoardOperations } from '../../board-runtime';
import { useBoardPermission } from '../../board-host';
import { presidingSeatId } from './presiding-decode';
import { WhoPresidedRow } from './who-presided';

const ATTENDANCE_OPTIONS: readonly { value: BoardMeetingAttendanceValue; label: string }[] = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'Absent' },
];

export interface AttendancePanelProps {
  orgRef: string;
  meetingId: string;
  /** The meeting's day: the seats that served on it are the ones listed (CBG-FIX41). */
  servedOn: MeetingDay | null;
  /** Frozen once the board has approved the meeting's minutes — see the record page's header. */
  readOnly: boolean;
}

export function AttendancePanel({ orgRef, meetingId, servedOn, readOnly }: AttendancePanelProps) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  // Seats are added on the roster (`board_member:manage`); the empty line points there only for
  // someone who can.
  const canManageRoster = useBoardPermission('board_member:manage');
  const [editing, setEditing] = useState<OrgBoardMemberOut | null>(null);

  const attendanceOptions = operations.officerListBoardMeetingAttendanceOptions({
    path: { org_ref: orgRef, meeting_id: meetingId },
  });
  const attendanceQuery = useQuery({ ...attendanceOptions, select: extractAttendance });

  const intentsQuery = useQuery({
    ...operations.officerListBoardAttendanceIntentsOptions({
      path: { org_ref: orgRef, meeting_id: meetingId },
    }),
    select: (body: unknown) => extractList<OrgBoardAttendanceIntentOut>(body, 'data'),
  });

  // The whole roster (one cache entry with the record page's own read), offered by the meeting's day.
  const rosterQuery = useQuery({
    ...operations.officerListBoardMembersOptions({
      path: { org_ref: orgRef },
      query: { include_removed: true },
    }),
    select: (body: unknown) => sortSeats(extractList<OrgBoardMemberOut>(body, 'data')),
  });

  const recorded = attendanceBySeat(attendanceQuery.data ?? []);
  const roster = seatsServingOn(rosterQuery.data ?? [], servedOn, new Set(recorded.keys()));
  // Who presided, and the only people who can be chosen: those recorded as present, in this order.
  const presidingId = presidingSeatId(attendanceQuery.data ?? []);
  const present = roster.filter((seat) => recorded.get(seat.id)?.attendance === 'present');
  const intents = new Map(
    (intentsQuery.data ?? []).map((intent) => [intent.board_member_id, intent]),
  );
  // CBG-YR3-VOTENOTE (§V8, the orchestrator's V-C5): beside a serving director whose recorded term
  // end is before the meeting's day and still before today, the approved holdover line. Words only:
  // who may be recorded here, and who counts toward quorum, are unchanged.
  const today = todayAsDateInput();
  const heldOver = (seat: OrgBoardMemberOut): string | null => {
    if ((seat.status ?? '') !== 'active' || !isDirector(seat)) return null;
    const ended = termEndedBefore(seat.term_end_on, servedOn?.day ?? null);
    return ended !== null && termEndedBefore(ended, today) !== null ? ended : null;
  };

  return (
    <Card testId="attendance-panel">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              Who was there
            </h2>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              Your record of who was actually in the room, beside what each seat told you in
              advance.
            </p>
          </div>
        </div>

        {roster.length === 0 ? (
          <p
            className="font-sans text-s13 font-w400 tracking-givmo text-text-tertiary"
            data-testid="attendance-empty"
          >
            {rosterQuery.isPending
              ? 'Loading the roster…'
              : canManageRoster
                ? 'No seats on the roster yet. Add them on the roster page first.'
                : 'No seats on the roster yet.'}
          </p>
        ) : (
          <div className="flex flex-col gap-8">
            <WhoPresidedRow
              orgRef={orgRef}
              meetingId={meetingId}
              presidingId={presidingId}
              roster={rosterQuery.data ?? []}
              present={present}
              readOnly={readOnly}
              onRecorded={() =>
                void queryClient.invalidateQueries({ queryKey: attendanceOptions.queryKey })
              }
            />
            <ul className="flex flex-col gap-8" data-testid="attendance-list">
              {roster.map((seat) => {
                const row = recorded.get(seat.id) ?? null;
                const intent = intents.get(seat.id) ?? null;
                const heldOverOn = heldOver(seat);
                return (
                  <li
                    key={seat.id}
                    className="flex flex-wrap items-center justify-between gap-12 rounded-lg border border-border-subtle p-12"
                    data-testid={`attendance-row-${seat.id}`}
                  >
                    {/* `min-w-0 flex-1`: a long line of titles wraps, and the act stays at the row's end. */}
                    <div className="flex min-w-0 flex-1 flex-col gap-4">
                      <div className="flex flex-wrap items-center gap-8">
                        <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                          {seat.display_name}
                        </span>
                        {seat.status === 'removed' ? (
                          // Enterable anyway: a director removed in the autumn was in the room in the
                          // spring, and an organization recording that meeting afterwards is recording a
                          // true fact.
                          <Badge tone="neutral">No longer serving</Badge>
                        ) : null}
                        {row !== null ? (
                          <Badge tone={attendanceTone(row.attendance)}>
                            {attendanceLabel(row.attendance)}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">Not recorded</Badge>
                        )}
                      </div>

                      {/* The person's facts (CBG-YR3-ROLES layer 1): present and in the minutes either
                        way, counted toward quorum only as a director. */}
                      <span
                        className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                        data-testid={`attendance-capacity-${seat.id}`}
                      >
                        {capacityLine(seat)}
                      </span>

                      {/* One phrase, kept on one line wherever the row narrows (cut votenote's
                        review, F1: at 390 the column broke it after "still"). */}
                      {heldOverOn !== null ? (
                        <span
                          className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                          data-testid={`attendance-holdover-${seat.id}`}
                        >
                          {keptTogether(l3.termEndedStillServing(displayDate(heldOverOn)))}
                        </span>
                      ) : null}

                      {row !== null && (row.arrived_at ?? row.departed_at) ? (
                        <span
                          className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                          data-testid={`attendance-times-${seat.id}`}
                        >
                          {/* Two instants, never folded into a status word — one seat can have both. */}
                          {row.arrived_at ? `Arrived ${displayInstant(row.arrived_at)}` : null}
                          {row.arrived_at && row.departed_at ? ' · ' : null}
                          {row.departed_at ? `Left ${displayInstant(row.departed_at)}` : null}
                        </span>
                      ) : null}

                      {row?.note ? (
                        <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                          {row.note}
                        </span>
                      ) : null}

                      {intent !== null ? (
                        <span
                          className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary"
                          data-testid={`attendance-intent-${seat.id}`}
                        >
                          {/* ⚠ The RSVP now has TWO capture classes — the director's own answer and an
                            officer's entry on their behalf — and §3.7's rule is that they never
                            share a label. This column is read-only reference (the write lives on the
                            meeting page, where an expectation belongs), but an unlabelled answer
                            here would present an officer's entry as the director's own, which is
                            exactly the false record the rule exists to prevent. */}
                          {intentLabel(intent.intent)} · {intentCaptureLine(intent)}
                        </span>
                      ) : null}
                    </div>

                    {!readOnly ? (
                      <Button
                        appearance="saas"
                        tone="secondary"
                        onClick={() => setEditing(seat)}
                        testId={`attendance-record-${seat.id}`}
                      >
                        {row === null ? 'Record' : 'Change'}
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardBody>

      {editing !== null ? (
        <RecordAttendanceDialog
          orgRef={orgRef}
          meetingId={meetingId}
          seat={editing}
          existing={recorded.get(editing.id) ?? null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: attendanceOptions.queryKey });
          }}
        />
      ) : null}
    </Card>
  );
}

function RecordAttendanceDialog({
  orgRef,
  meetingId,
  seat,
  existing,
  onClose,
  onSaved,
}: {
  orgRef: string;
  meetingId: string;
  seat: OrgBoardMemberOut;
  existing: BoardMeetingAttendanceOut | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const operations = useBoardOperations();
  const [attendance, setAttendance] = useState<BoardMeetingAttendanceValue>(
    (existing?.attendance as BoardMeetingAttendanceValue | undefined) ?? 'present',
  );
  const [arrivedAt, setArrivedAt] = useState(toLocalInput(existing?.arrived_at));
  const [departedAt, setDepartedAt] = useState(toLocalInput(existing?.departed_at));
  const [note, setNote] = useState(existing?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardMeetingAttendance({
        path: { org_ref: orgRef, meeting_id: meetingId, board_member_id: seat.id },
        body: {
          attendance,
          // An empty field is an ABSENT instant, not midnight. `undefined` leaves the backend's
          // column null; sending an empty string would be a 422 and sending a synthesized time would
          // put a moment in the organization's record that nobody observed.
          arrived_at: fromLocalInput(arrivedAt),
          departed_at: fromLocalInput(departedAt),
          note: note.trim().length > 0 ? note.trim() : null,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('Recorded.');
      onSaved();
    },
    onError: (caught) => {
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.',
      );
    },
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={`Record attendance for ${seat.display_name}`}
        className="flex flex-col gap-16"
        testId="attendance-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {seat.display_name}
        </Dialog.Title>

        <Select
          label="In the room"
          appearance="saas"
          value={attendance}
          onValueChange={(next) =>
            next !== null && setAttendance(next as BoardMeetingAttendanceValue)
          }
          options={ATTENDANCE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          testId="attendance-value"
        />

        <TextField
          label="Arrived (optional)"
          type="datetime-local"
          value={arrivedAt}
          onChange={(event) => setArrivedAt(event.target.value)}
          testId="attendance-arrived"
        />
        <TextField
          label="Left (optional)"
          type="datetime-local"
          value={departedAt}
          onChange={(event) => setDepartedAt(event.target.value)}
          testId="attendance-departed"
        />
        <FieldNote testId="attendance-times-note">
          Fill these in only if this seat was not present for the whole meeting. A vote records who
          was in the room at the moment it was taken, so these times are what decides that.
        </FieldNote>

        <TextField
          label="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          testId="attendance-note"
        />

        {error !== null ? <ErrorMessage testId="attendance-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="attendance-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={save.isPending}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            testId="attendance-save"
          >
            Record
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * An ISO instant as a `datetime-local` field value, in the reader's own zone.
 *
 * `<input type="datetime-local">` has no zone: it shows and returns wall-clock text. The officer is
 * typing the time they saw on the wall, so the reader's zone is the right one to render into and to
 * read back out of — the round trip is what keeps "arrived at 7:05" meaning 7:05 to the person who
 * entered it.
 */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => `${value}`.padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/** The field's wall-clock text back to an ISO instant, or `null` when the officer left it empty. */
function fromLocalInput(value: string): string | null {
  if (value.trim().length === 0) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
